import type { ReactNode, SelectHTMLAttributes } from "react";

import { cn } from "../lib/cn.js";
import { SELECT_CHROME, SelectChevron } from "./SelectChevron.js";
import { fieldClassName } from "./TextField.js";

/**
 * A select that COLLECTS a value. `TextField`'s sibling, and the form half of the pair
 * `FilterSelect` is the chrome half of.
 *
 * ==========================================================================================
 * IT EXISTS BECAUSE EVERY FORM SELECT IN THE APPLICATION WAS HAND-ROLLED, AND WRONG
 * ==========================================================================================
 *
 * Twenty-six of them, across savings, identity, expenses, income, properties, investments and
 * vehicles, all carrying the same copied line:
 *
 * a `rounded-md` box whose border colour and background were bracketed references to two custom
 * properties, `--border` and `--surface`.
 *
 * **`--border` and `--surface` do not exist.** The tokens are `--border-default` and `--bg-surface`,
 * and neither of those spellings is what those classes ask for — so every form select in GLADIS has
 * been rendering with no declared border colour and no background at all, at a radius and a height
 * that match nothing else on the page. It is `text-danger` again, one syntax along:
 * `colour-tokens-exist.test.ts` could not see it because its pattern requires a letter after the
 * prefix and these are bracketed arbitrary values. That hole is closed in the same change as this
 * component.
 *
 * ==========================================================================================
 * IT IS NOT `FilterSelect` WITH A DIFFERENT NAME, AND THAT DISTINCTION IS THE REPO'S OWN
 * ==========================================================================================
 *
 * They share their geometry and their chevron — `SelectChevron` is one file for exactly that reason
 * — and they differ in fill and border, deliberately. `FilterSelect`'s own header states the rule
 * from the other side: "`TextField`'s filled `bg-input/30` is right for a form and wrong here, which
 * is why the search box is a bare `<input>` wearing this instead." The inverse is what this
 * component is: an unfilled, hairline control in among filled fields reads as a field somebody
 * forgot to complete, which is precisely the complaint that produced the search box's treatment.
 *
 * So the appearance comes from `fieldClassName` and nothing is overridden. Every colour in this
 * control is stated exactly once, by the same string `TextField` and `MoneyInput` use, and a change
 * to the field's look reaches all three at once — which is the property twenty-six copies never had.
 */
export type SelectFieldProps = SelectHTMLAttributes<HTMLSelectElement>;

export function SelectField({ className, children, ...rest }: SelectFieldProps): ReactNode {
  return (
    // The wrapper positions the chevron against the control rather than against the form row, so a
    // select in a two-column grid keeps its arrow inside its own box.
    <span className="relative flex items-center">
      <select className={cn(fieldClassName, SELECT_CHROME, className)} {...rest}>
        {children}
      </select>
      <SelectChevron />
    </span>
  );
}
